/**
 * Failed/voided payments alert banner (MED-519 / E13-7 AC12).
 * Surfaces failed payments prominently with retry action.
 *
 * Failures are grouped by coach so a coach with multiple failed underlying
 * payments shows as a single Retry action rather than one button per row.
 */

import { useMemo, useRef, useState } from 'react';
import { Alert, Group, Text, Button, Badge } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, RefreshCw } from 'react-feather';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { BillComPayment } from '../../../../services/api/billcom';

interface FailedPaymentsAlertProps {
  cohortId: string;
  failedPayments: BillComPayment[];
  onRetrySuccess: () => void;
}

interface CoachFailureGroup {
  coachUserId: string;
  coachName: string;
  paymentIds: string[];
}

export function FailedPaymentsAlert({
  cohortId,
  failedPayments,
  onRetrySuccess,
}: FailedPaymentsAlertProps) {
  // One retry batch at a time. A second click while a batch runs would retry the same
  // failed rows again and can create a duplicate bill. The ref blocks a fast double
  // click before the re-render disables the buttons.
  const [retryingCoachId, setRetryingCoachId] = useState<string | null>(null);
  const retryingRef = useRef(false);

  const groups = useMemo<CoachFailureGroup[]>(() => {
    const byCoach = new Map<string, CoachFailureGroup>();
    for (const p of failedPayments) {
      const existing = byCoach.get(p.coach_user_id);
      if (existing) {
        existing.paymentIds.push(p.id);
      } else {
        byCoach.set(p.coach_user_id, {
          coachUserId: p.coach_user_id,
          coachName: p.coach_name || 'Unknown',
          paymentIds: [p.id],
        });
      }
    }
    return Array.from(byCoach.values());
  }, [failedPayments]);

  if (groups.length === 0) return null;

  // Run retries ONE AT A TIME. Bill.com enforces a per-organization concurrent-request
  // cap (BDC_1322) and rejects the overflow, so firing a coach's whole batch in parallel
  // caused most payments to fail. Sequential is slower but reliable.
  const runRetries = async (
    group: CoachFailureGroup,
  ): Promise<PromiseSettledResult<unknown>[]> => {
    const results: PromiseSettledResult<unknown>[] = [];
    for (const id of group.paymentIds) {
      try {
        const value = await cohortBillcomApi.retryFailedPayment(cohortId, id);
        results.push({ status: 'fulfilled', value });
      } catch (reason) {
        results.push({ status: 'rejected', reason });
      }
    }
    return results;
  };

  const handleRetry = async (group: CoachFailureGroup) => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetryingCoachId(group.coachUserId);
    try {
      await retryGroup(group);
    } finally {
      retryingRef.current = false;
      setRetryingCoachId(null);
    }
  };

  const retryGroup = async (group: CoachFailureGroup) => {
    const results = await runRetries(group);

    const failed = results.filter((r) => r.status === 'rejected');
    const succeeded = results.length - failed.length;

    if (failed.length === 0) {
      notifications.show({
        title: 'Payment Retried',
        message:
          succeeded === 1
            ? `${group.coachName}'s payment has been re-submitted to Bill.com`
            : `All ${succeeded} payments for ${group.coachName} have been re-submitted to Bill.com`,
        color: 'medleyGreen',
      });
    } else {
      const firstError = failed[0] as PromiseRejectedResult;
      const reason =
        firstError.reason instanceof Error ? firstError.reason.message : 'Failed to retry payment';
      notifications.show({
        title: succeeded > 0 ? 'Some Retries Failed' : 'Retry Failed',
        message:
          succeeded > 0
            ? `${succeeded} succeeded, ${failed.length} failed. First error: ${reason}`
            : reason,
        color: 'red',
      });
    }
    onRetrySuccess();
  };

  return (
    <Alert
      color="dangerRed"
      variant="light"
      icon={<AlertTriangle size={16} />}
      styles={{ root: { borderRadius: 8 } }}
    >
      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="sm" fw={600}>
            {failedPayments.length} Payment{failedPayments.length > 1 ? 's' : ''} Failed
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {groups
              .map((g) =>
                g.paymentIds.length > 1 ? `${g.coachName} (${g.paymentIds.length})` : g.coachName,
              )
              .join(', ')}
          </Text>
        </div>
        <Group gap="xs">
          {groups.length <= 3 &&
            groups.map((g) => (
              <Button
                key={g.coachUserId}
                size="xs"
                variant="light"
                color="dangerRed"
                leftSection={<RefreshCw size={12} />}
                onClick={() => handleRetry(g)}
                loading={retryingCoachId === g.coachUserId}
                disabled={retryingCoachId !== null && retryingCoachId !== g.coachUserId}
              >
                {g.paymentIds.length > 1 ? `Retry (${g.paymentIds.length})` : 'Retry'}
              </Button>
            ))}
          {groups.length > 3 && (
            <Badge color="dangerRed" variant="filled" size="lg">
              {groups.length} coaches failed
            </Badge>
          )}
        </Group>
      </Group>
    </Alert>
  );
}
